import React, { memo } from 'react';
import { Hospital, EditFormData, MapModalState, InsuranceItem } from '../../types/hospital.types';
import HospitalHeader from './HospitalHeader';
import HospitalDetails from './HospitalDetails';
import './HospitalCard.css';

interface HospitalCardProps {
  hospital: Hospital;
  isEditing: boolean;
  isSaving: boolean;
  isExpanded: boolean;
  editFormData: EditFormData;
  hasChanges: boolean;
  onToggleExpand: (id: string) => void;
  onToggleEdit: (hospital: Hospital, onSave: (id: string, data: any) => void, onRefreshCounts?: () => void) => void;
  onInputChange: (field: keyof EditFormData, value: string | boolean | InsuranceItem[]) => void;
  onMapOpen: (modal: MapModalState) => void;
  onUpdateHospital: (id: string, data: any) => void;
  onRefreshCounts: () => void;
}

const HospitalCard: React.FC<HospitalCardProps> = memo(({
  hospital,
  isEditing,
  isSaving,
  isExpanded,
  editFormData,
  hasChanges,
  onToggleExpand,
  onToggleEdit,
  onInputChange,
  onMapOpen,
  onUpdateHospital,
  onRefreshCounts
}) => {
  const handleToggleEdit = () => {
    if (!isExpanded) {
      onToggleExpand(hospital._id);
    }
    onToggleEdit(hospital, onUpdateHospital, onRefreshCounts);
  };

  const handleMapOpen = () => {
    onMapOpen({
      isOpen: true,
      coordinates: hospital.location.coordinates,
      hospitalName: hospital.name,
      hospitalId: hospital._id
    });
  };

  const getCardTypeClass = () => {
    if (hospital.handleEmergency) return 'pending';
    if (hospital.isEsic) return 'approved';
    if (!hospital.isDeleted) return 'active';
    return 'total';
  };

  return (
    <div className={`hospital-card ${getCardTypeClass()} ${isEditing ? 'editing' : ''}`}>
      <div className="card-header" onClick={() => onToggleExpand(hospital._id)}>
        <HospitalHeader
          hospital={hospital}
          isEditing={isEditing}
          isSaving={isSaving}
          isExpanded={isExpanded}
          hasChanges={hasChanges}
          onToggleEdit={handleToggleEdit}
        />
      </div>

      {isExpanded && (
        <HospitalDetails
          hospital={hospital}
          isEditing={isEditing}
          editFormData={editFormData}
          onInputChange={onInputChange}
          onMapOpen={handleMapOpen}
        />
      )}
    </div>
  );
});

HospitalCard.displayName = 'HospitalCard';

export default HospitalCard;